import { InputGroup, Label } from '@slal/ui/forms';
import { FormProvider, yupResolver } from '@slal/ui/react-hook-form/core';
import { Field, FieldSet, TextField } from '@slal/ui/react-hook-form/fields';
import { normalizeMonetaryAmount, normalizeNumbers } from '@slal/ui/react-hook-form/utils';
import { P } from '@slal/ui/typography';
import { useNavigate } from 'react-router';
import { MaxWidthContent, Notification, StyledForm } from '~/components';
import { useApplicationContext, useApplicationForm, useNormalizations } from '~/hooks';
import { regularIncomeValidationSchema } from '~/schemas';
import { ApplicationFormKeys, RegularIncomeSchema } from '~/types';
import { ApplicationButtons } from '../ApplicationButtons';
import { IncomeTax } from './IncomeTax';

export const RegularIncomeForm = () => {
    const {
        pensionValues: { maximumRegularIncome },
        withdrawalOption,
    } = useApplicationContext();

    const { handleSubmit, form } = useApplicationForm<RegularIncomeSchema>(
        {
            resolver: yupResolver(regularIncomeValidationSchema(maximumRegularIncome)),
        },
        ApplicationFormKeys.REGULAR_INCOME
    );

    const navigate = useNavigate();
    const pathBack = withdrawalOption === 'Both' ? 'lump-sum-options' : 'withdrawal-options';

    const onValidCallback = () => navigate(`/application/investment-options`);
    const { normalizeMonetaryAmount: normalizeMoney } = useNormalizations({ normalizeMonetaryAmount });

    return (
        <MaxWidthContent>
            <IncomeTax />
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit(onValidCallback)}>
                    <FieldSet aria-label="Regular income options">
                        <Field label="Monthly income" labelFor="regularIncome.amount">
                            <InputGroup>
                                <Label>£</Label>
                                <TextField name="regularIncome.amount" setValueAs={normalizeMoney} />
                            </InputGroup>
                        </Field>
                        <Field
                            helpMessage="Must be between 1 and 28"
                            label="What day of the month would you like your income to be paid?"
                            labelFor="regularIncome.day"
                        >
                            <TextField size={2} name="regularIncome.day" setValueAs={normalizeNumbers} />
                        </Field>
                        <Notification level="info">
                            <P>
                                If you choose a date within the next 5 days payment may be delayed. We'll make your
                                first payment as soon as we can and subsequent payments will be paid on your chosen
                                date.
                            </P>
                        </Notification>
                    </FieldSet>
                    <ApplicationButtons backTo={`/application/${pathBack}`} />
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};
